import { ReactNode, useState } from 'react';
import { Button } from '@components/button';
import { Box, SvgIcon, Typography } from '@mui/material';
import { Menu, IMenuItem } from '@components/menu';
import { IconButton } from '@components/iconButton';
import { Tooltip } from '@components/tooltip/Tooltip';
import { EllipsisH, QuestionCircle, Minus, Plus } from '@assets/svgs';
import * as styles from '@components/quotePanel/QuotePanel.styles';
import { PanelTitles } from '@models/quotePanel';

interface IPanelHeader {
  title: string;
  color?: string;
  icon?: ReactNode;
  headerActions?: ReactNode;
  actionContent?: ReactNode;
  menuContent?: ReactNode;
  menuItems?: IMenuItem[];
  helpText?: string | null;
  onEdit?: () => void;
  allowEdit?: boolean;
  isMinimized?: boolean;
  showMinimizeButton?: boolean;
  handleMinimized?: () => void;
  minimizedEnabled: boolean;
}

export const PanelHeader = ({
  title,
  color,
  icon,
  headerActions,
  actionContent,
  menuContent,
  menuItems,
  helpText,
  onEdit,
  allowEdit,
  isMinimized,
  handleMinimized,
  showMinimizeButton,
  minimizedEnabled,
  ...other
}: IPanelHeader) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleOnClickMenuItem = (index: number) => {
    menuItems?.[index]?.action?.();
    setAnchorEl(null);
  };

  return (
    <Box sx={styles.container} {...other}>
      <Box sx={styles.subContainerLeft}>
        {icon && (
          <IconButton
            disabled
            data-testid='panel-status-icon'
            sx={{
              marginRight: '8px',
              display: 'inherit',
              svg: {
                color,
              },
            }}
          >
            {icon}
          </IconButton>
        )}
        <Typography variant='h4' sx={styles.text}>
          {title}
        </Typography>
        {headerActions}
      </Box>
      <Box sx={styles.subContainerRight}>
        {actionContent}
        {onEdit && allowEdit && (
          <Button variant='text' onClick={onEdit} sx={{ padding: '8px 8px' }}>
            Edit
          </Button>
        )}
        {helpText && (
          <Tooltip placement='bottom' title={helpText} sx={{ maxHeight: 'none' }}>
            <IconButton
              sx={{
                color: 'icon.dark',
              }}
            >
              <QuestionCircle />
            </IconButton>
          </Tooltip>
        )}
        {menuContent}
        {title !== PanelTitles.Location && showMinimizeButton && (
          <IconButton
            sx={{
              ...styles.icon,
              color: minimizedEnabled ? 'icon.dark' : 'common.gray',
            }}
            onClick={handleMinimized}
            disabled={!minimizedEnabled}
          >
            {isMinimized ? <Plus /> : <Minus />}
          </IconButton>
        )}
        {menuItems && (
          <IconButton sx={styles.icon} onClick={handleMenuClick}>
            <SvgIcon
              sx={{
                color: 'icon.dark',
              }}
            >
              <EllipsisH x='6px' y='6px' width='12px' height='12px' />
            </SvgIcon>
          </IconButton>
        )}
      </Box>
      {menuItems && (
        <Menu
          open={!!anchorEl}
          anchorEl={anchorEl}
          onClose={handleMenuClose}
          onClickMenuItem={handleOnClickMenuItem}
          items={menuItems}
        />
      )}
    </Box>
  );
};
